import { useQuery } from "@apollo/client";
import odooIcon from "assets/odoo-icon.svg";
import { CheckBox } from "components/base/base";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import { Select, Switcher } from "components/base/Form";
import SearchBar from "components/base/SearchBar";
import Spinner from "components/base/Spinner";
import { useModals } from "ModalProvider";
import moment from "moment";
import { createContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import EditProductView from "./EditProductView";
import { FETCH_WIS_PRODUCTS } from "./graphql";
import NewProducts from "./NewProducts";
import BatchUpdateForm from "./ProductBatchUpdate";
import ProductLoader from "./ProductLoader";

export const AllProductsContext = createContext({});

const PAGE_SIZE = 50;

const AllProducts = () => {
  const [searchParams, setSearchParams] = useSearchParams({
    offset: 0,
    q: "",
    company: "ALL",
  });
  const id = searchParams.get("id");
  const q = searchParams.get("q");
  const company = searchParams.get("company");
  const offset = parseInt(searchParams.get("offset")) || 0;

  const params = Object.fromEntries(searchParams.entries());

  const [isActiveOnly, setIsActiveOnly] = useState(true);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [allSelected, setAllSelected] = useState(false);

  const { loading, error, data } = useQuery(FETCH_WIS_PRODUCTS, {
    variables: { offset, limit: PAGE_SIZE, isActiveOnly, q, company },
  });

  const newModal = useModals();
  const editModal = useModals();

  useEffect(() => {
    if (data) {
      if (id && id !== "null") {
        editProduct({ id });
      }
    }
  }, [data]);

  useEffect(() => {
    setAllSelected(selectedProducts.length === data?.products?.results.length);
  }, [selectedProducts, data]);

  function editProduct(product) {
    setSearchParams({ ...params, id: product.id });
    console.log("editProduct", product);
    editModal.present({
      title: `Edit Product: #${product.id}`,
      fullscreen: true,
      isBeingPresented: true,
      onDismiss: () => setSearchParams({ company, q, offset }),
      children: (
        <ProductLoader
          id={product.id}
          hide={editModal.hide}
          duplicateProduct={duplicateProduct}
        />
      ),
    });
  }

  function duplicateProduct(sourceProduct) {
    setSearchParams(params);
    editModal.hide();
    newModal.present({
      title: "Duplicate Product",
      fullscreen: true,
      BeingPresented: true,
      children: (
        <EditProductView
          onSaved={onCreatedProduct}
          initialValues={sourceProduct}
        />
      ),
    });
  }

  function handleSelectProduct(product) {
    // if the product is already in the selected list, remove it. Otherwise, add it.
    if (selectedProducts.some((p) => p.id === product.id)) {
      setSelectedProducts((prev) => prev.filter((p) => p.id !== product.id));
    } else {
      setSelectedProducts((prev) => [...prev, product]);
    }
  }

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(data.products.results);
    }
  };

  function createHandler() {
    newModal.present({
      title: "Create Product",
      fullscreen: true,
      BeingPresented: true,
      children: <EditProductView onSaved={onCreatedProduct} />,
    });
  }

  function onCreatedProduct(product) {
    newModal.hide();
    editProduct(product);
  }

  return (
    <div className="flex space-x-8 h-full p-6">
      <div className="flex-1 overflow-auto flex flex-col card p-0 rounded-2xl relative">
        <div className="flex items-center sticky top-0 p-6 backdrop-blur-lg space-x-4 z-10">
          <div>
            <Select
              value={company}
              onChange={(e) =>
                setSearchParams({
                  offset: 0,
                  company: e.target.value,
                  q,
                })
              }
            >
              <option value="ALL">All Companies</option>
              <option>AB</option>
              <option>LTD</option>
              <option>UNSET</option>
            </Select>
          </div>
          <div className="flex-1">
            <SearchBar
              autoFocus
              initialQuery={q}
              className="p-2 flex-1 bg-gray-100 dark:bg-gray-700 dark:bg-opacity-30"
              onChange={(q) => setSearchParams({ company, offset: 0, q })}
              placeholder="Search produc by name, item number or odoo id."
            />
          </div>

          <Button onClick={createHandler}>+ Create Product</Button>

          <div className="flex items-center space-x-2">
            <label htmlFor="">Active Only: </label>
            <Switcher
              isOn={isActiveOnly}
              onChange={() => setIsActiveOnly((prev) => !prev)}
            />
          </div>
        </div>

        <ProductsResult
          loading={loading}
          error={error}
          company={company}
          data={data}
          offset={offset}
          setSearchParams={setSearchParams}
          editProduct={editProduct}
          q={q}
          selectedProducts={selectedProducts}
          onSelectProduct={handleSelectProduct}
          allSelected={allSelected}
          onSelectAll={handleSelectAll}
          onClearSelection={() => setSelectedProducts([])}
        />
      </div>

      <NewProducts editProduct={editProduct} />
    </div>
  );
};

const ProductsResult = ({
  loading,
  error,
  data,
  company,
  offset,
  q,
  setSearchParams,
  editProduct,
  selectedProducts,
  onSelectProduct,
  allSelected,
  onSelectAll,
  onClearSelection,
}) => {
  const batchUpdateModal = useModals();

  if (loading)
    return (
      <div className="p-10 relative">
        <Spinner />
      </div>
    );
  if (error) return <Errors error={error} />;

  const { results, total } = data.products;

  const products = results.map((i) => ({
    ...i,
    isNew: moment.duration(moment().diff(moment(i.createdAt))).asDays() < 1,
  }));

  return (
    <div className="h-full overflow-auto flex flex-col">
      {selectedProducts.length > 0 && (
        <div className="px-8 flex items-center space-x-2 pb-2">
          <CheckBox checked={allSelected} onChange={onSelectAll} />
          <Button
            onClick={() => {
              batchUpdateModal.present({
                title: "Batch Update",
                isBeingPresented: true,
                maxWidth: "max-w-6xl",
                center: true,
                children: (
                  <BatchUpdateForm
                    products={selectedProducts}
                    hide={batchUpdateModal.hide}
                  />
                ),
              });
            }}
          >
            Batch Update ({selectedProducts.length})
          </Button>

          <Button onClick={onClearSelection} className="pl-2">
            Clear Selection
          </Button>
        </div>
      )}

      <div
        className="px-8 flex-1 overflow-auto"
        data-testid="manage-product-list"
      >
        {products.map((p) => (
          <ProductRow
            product={p}
            key={p.id}
            editProduct={() => editProduct(p)}
            isSelected={selectedProducts.some((sp) => sp.id === p.id)}
            onSelectProduct={onSelectProduct}
          />
        ))}
      </div>

      <div className="p-6 flex justify-end items-center space-x-2 opacity-60">
        {offset > 0 ? (
          <Button
            prev
            onClick={() =>
              setSearchParams({ company, q, offset: offset - PAGE_SIZE })
            }
          />
        ) : null}
        <div>
          {offset + 1}-{Math.min(PAGE_SIZE + offset, total)} / {total}
        </div>
        {total > offset + PAGE_SIZE ? (
          <Button
            next
            onClick={() =>
              setSearchParams({ company, q, offset: PAGE_SIZE + offset })
            }
          />
        ) : null}
      </div>
    </div>
  );
};

export const ProductRow = ({
  product,
  editProduct,
  isSelected,
  onSelectProduct,
}) => {
  return (
    <div
      className={`border-b border-gray-100 dark:border-gray-700 py-2 cursor-pointer hover:bg-gray-100 active:bg-gray-200 dark:hover:bg-gray-800 dark:first-line:active:bg-gray-900 flex items-center space-x-2
         ${product.isActive ? "" : "opacity-30"}
   `}
      onClick={editProduct}
    >
      {isSelected !== undefined && onSelectProduct !== undefined && (
        <CheckBox
          checked={isSelected}
          onChange={() => {
            onSelectProduct(product);
          }}
        />
      )}
      {product.isNew ? (
        <div className="rounded-full text-white bg-green-500 dark:bg-green-600 text-xs px-2 py-1">
          new
        </div>
      ) : null}

      {product.odooId ? (
        <img
          className="mr-2"
          style={{ height: 16 }}
          src={odooIcon}
          alt="odoo product"
        />
      ) : null}
      <div>
        <span>
          ({product.company}) [{product.number}] {product.name}
          {product.version && ` | v${product.version}`}
        </span>
        <span className="opacity-50 pl-2">
          W#{product.id},OD#
          {product.odooId ? product.odooId : "?"}
        </span>
      </div>
      <div className="flex items-center space-x-2">
        {product.images
          ? product.images.map((x, index) => (
              <img className="h-4" key={index} src={x.url} />
            ))
          : null}
      </div>
    </div>
  );
};

export default AllProducts;
